import { useCallback, useEffect } from 'react';
import { useImmerAtom, useSetImmerAtom } from 'jotai-immer';
import { useCurrentStateAndParams } from '@uirouter/react';
import {
    splitOrderInvoicesDetailModelAtom,
    splitOrderInvoicesDetailPageStateAtom,
    splitOrderInvoicesDetailLineItemsAtom,
} from './split_order_invoices_detail_atoms';
import splitOrderInvoicesRepo from '../data/splitOrderInvoicesRepo';
import { ngContext, ngDelegate } from '../../../core/services/ng.delegate';
import { SplitOrderInvoicesDetailModel } from './split_order_invoices_detail_types';
import splitOrderInvoicesDetailApiToVM from './splitOrderInvoicesDetailApiToVM';

export const useSplitOrderInvoicesDetailsInit = (): { title: string; fetchInvoice: (id: string) => void } => {
    const [state, setState] = useImmerAtom(splitOrderInvoicesDetailPageStateAtom);
    const setModel = useSetImmerAtom(splitOrderInvoicesDetailModelAtom);
    const setLineItems = useSetImmerAtom(splitOrderInvoicesDetailLineItemsAtom);
    const { params: routeParams } = useCurrentStateAndParams();

    useEffect(() => {
        const ctx = ngContext();
        setState(draft => {
            draft.userType = ctx.User?.Type ?? '';
            draft.userAffList = ctx.User?.AffList ?? [];
            draft.isVeriSaveOnly = !!ctx.User?.VeriSaveOnly;

            // Breadcrumb return: pick up params from route data
            if (routeParams.id && !draft.id) {
                draft.id = routeParams.id;
            }
            if (routeParams.rebatesOnly != null) {
                draft.rebatesOnly = !!routeParams.rebatesOnly;
            }
            if (routeParams.lineitemselected) {
                draft.lineitemselected = routeParams.lineitemselected;
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getModel = useCallback(
        async (id: string): Promise<void> => {
            try {
                ngDelegate.wait(true, true);
                const res: SplitOrderInvoicesDetailModel = await splitOrderInvoicesRepo.getDetail(id);
                const items = splitOrderInvoicesDetailApiToVM(res.Items);
                setModel(() => res);
                setLineItems(() => ({
                    items,
                    dataItems: items,
                }));
            } catch (err: any) {
                ngDelegate.warn(
                    'Failed to load split invoice detail',
                    'useSplitOrderInvoicesDetailsInit',
                    String(err?.status || ''),
                );
            } finally {
                setState(draft => {
                    draft.waiting = false;
                });
                ngDelegate.wait(false);
            }
        },
        [setModel, setLineItems, setState],
    );

    useEffect(() => {
        if (state.id) {
            getModel(state.id);
        }
    }, [state.id, getModel]);

    return {
        title: state.title || 'Split Invoice Detail',
        fetchInvoice: getModel,
    };
};
